import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Order, Contact } from '../../types';
import { fetchOrders } from '../../redux/slices/orderSlice';
import * as orderService from '../../services/order-service';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import BackButton from '../../components/BackButton/BackButton';
import DataTable, { StatusColumn } from '../../components/DataTable/DataTable';
import styles from './OrderDetailsPage.module.css';

// Material UI imports
import {
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Payment as PaymentIcon,
  Home,
  DownloadForOffline as DownloadIcon,
  Print as PrintIcon,
  Contacts as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface OrderItemWithProduct {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  product?: {
    id: string;
    sku: string;
    name: string;
    description?: string;
    price?: number;
    specifications?: string;
    images?: Array<{
      image_url: string;
      alt_text: string;
      is_primary: boolean;
    }>;
  };
}

interface OrderWithDetails {
  id: string;
  quote_id: string;
  customer_id: string;
  shipping_address_id: string;
  payment_method: string;
  payment_term: string;
  status: 'Processing' | 'Shipped' | 'Delivered' | 'Cancelled';
  created_at?: string;
  quote?: {
    id: string;
    code: string;
    status: string;
    expiration_date: string;
    reseller_id: string;
    created_at: string;
    reseller: {
      id: string;
      name: string;
      email: string;
    };
  };
  customer?: {
    company_name: string;
    phone: string;
    email: string;
    cnpj: string;
    sector: string;
    id: string;
    reseller_id: string;
    created_at: string;
    contacts?: Contact[];
  };
  shipping_address?: {
    street: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    is_default: boolean;
    id: string;
    customer_id: string;
  };
  items?: OrderItemWithProduct[];
}

const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: orders } = useAppSelector(state => state.orders);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderWithDetails | null>(null);
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [openContactsModal, setOpenContactsModal] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        // First check if the order is already in the Redux store
        const orderInStore = orders.find(order => order.id === id) as OrderWithDetails;

        if (orderInStore) {
          setOrder(orderInStore);
        } else {
          // If not in store, fetch it from the API
          const response = await orderService.getOrderById(id);
          if (response.success) {
            setOrder(response.data as OrderWithDetails);
          } else {
            setError('Failed to fetch order details');
          }
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        setError('An error occurred while fetching order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id, orders]);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Pedidos', path: '/orders' },
      { label: order ? `Pedido ${order.id.substring(0, 8)}` : 'Pedido' },
    ]);
  }, [order, setBreadcrumbs]);

  const handleOpenContactsModal = () => {
    setOpenContactsModal(true);
  };

  const handleCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const getStatusChip = (status: string) => {
    /* eslint-disable */
    const statusMap: Record<
      string,
      {
        color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
        label: string;
      }
    > = {
      Processing: { color: 'info', label: 'Processando' },
      Shipped: { color: 'primary', label: 'Enviado' },
      Delivered: { color: 'success', label: 'Entregue' },
      Cancelled: { color: 'error', label: 'Cancelado' },
    };
    /* eslint-enable */
    const statusInfo = statusMap[status] || { color: 'default', label: status };
    return (
      <Chip label={statusInfo.label} color={statusInfo.color} size="small" variant="outlined" />
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handlePrintOrder = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // TODO: Implement PDF download
    enqueueSnackbar('Função de download em PDF será implementada em breve', {
      variant: 'info',
    });
  };

  const handleViewQuote = () => {
    if (order?.quote_id) {
      navigate(`/quotes/${order.quote_id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h5">{error || 'Pedido não encontrado'}</Typography>
        <Button onClick={() => navigate('/orders')} variant="contained" sx={{ mt: 2 }}>
          Voltar para pedidos
        </Button>
      </Box>
    );
  }

  // Calculate totals
  const subtotal = order.items
    ? order.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)
    : 0;

  return (
    <>
      <BackButton />

      {/* Header with order information */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5">Pedido {order.id.substring(0, 8)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {order.created_at &&
                new Date(order.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
            >
              <Button
                startIcon={<PrintIcon />}
                variant="outlined"
                size="small"
                onClick={handlePrintOrder}
                sx={{ borderRadius: '1px' }}
              >
                Imprimir
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                onClick={handleDownloadPDF}
                sx={{ borderRadius: '1px' }}
              >
                Baixar PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Order Status and Quote Info */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalShippingIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">Status do pedido</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status atual
              </Typography>
              {getStatusChip(order.status)}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Método de pagamento
              </Typography>
              <Typography variant="body1">{order.payment_method}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Prazo de pagamento
              </Typography>
              <Typography variant="body1">{order.payment_term}</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">Informações da cotação</Typography>
            </Box>
            {order.quote ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Código da cotação
                  </Typography>
                  <Typography variant="body1">{order.quote.code}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Data de criação
                  </Typography>
                  <Typography variant="body1">
                    {new Date(order.quote.created_at).toLocaleDateString('pt-BR')}
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleViewQuote}
                    sx={{ mt: 1, borderRadius: '1px' }}
                  >
                    Ver cotação original
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Informações da cotação não disponíveis
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Customer and Shipping Info */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">Informações do cliente</Typography>
              </Box>
              <Button
                color="primary"
                startIcon={<ContactsIcon />}
                onClick={handleOpenContactsModal}
                size="small"
              >
                Ver Contatos
              </Button>
            </Box>
            {order.customer ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Empresa
                  </Typography>
                  <Typography variant="body1">{order.customer.company_name}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    CNPJ
                  </Typography>
                  <Typography variant="body1">{order.customer.cnpj}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contato
                  </Typography>
                  <Typography variant="body1">{order.customer.email}</Typography>
                  <Typography variant="body1">{order.customer.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Setor
                  </Typography>
                  <Typography variant="body1">{order.customer.sector}</Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Informações do cliente não disponíveis
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">Endereço de entrega</Typography>
            </Box>
            {order.shipping_address ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">{order.shipping_address.street}</Typography>
                  <Typography variant="body1">
                    {`${order.shipping_address.city}, ${order.shipping_address.state} - ${order.shipping_address.zip_code}`}
                  </Typography>
                  <Typography variant="body1">{order.shipping_address.country}</Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Endereço de entrega não disponível
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Order Items */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PaymentIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">Itens do pedido</Typography>
        </Box>

        {order.items && order.items.length > 0 ? (
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Quantidade</TableCell>
                  <TableCell align="right">Preço unitário</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.product
                        ? item.product.name
                        : `Produto ${item.product_id.substring(0, 8)}`}
                    </TableCell>
                    <TableCell>{item.product ? item.product.sku : 'N/A'}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} />
                  <TableCell align="right">
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(subtotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Nenhum item encontrado para este pedido.
          </Typography>
        )}
      </Paper>

      {/* Contacts Modal */}
      <Dialog
        open={openContactsModal}
        onClose={handleCloseContactsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <ContactsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Contatos de {order?.customer?.company_name || 'Cliente'}
              </Typography>
            </Box>
            <IconButton
              aria-label="fechar"
              onClick={handleCloseContactsModal}
              sx={{
                color: theme => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {order?.customer?.contacts && order.customer.contacts.length > 0 ? (
            <Grid container spacing={2}>
              {order.customer.contacts.map((contact, index) => (
                <Grid item xs={12} sm={6} key={contact.id || index}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      border: contact.is_primary ? '2px solid #80c343' : '1px solid #e0e0e0',
                      position: 'relative',
                    }}
                  >
                    {contact.is_primary && (
                      <Chip
                        label="Principal"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: 8,
                          backgroundColor: '#80c343',
                        }}
                      />
                    )}
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon sx={{ mr: 1, color: '#80c343' }} />
                      <Typography variant="h6" fontWeight="medium">
                        {contact.name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.phone}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {contact.role}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              color="text.secondary"
            >
              <ContactsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Nenhum contato encontrado
              </Typography>
              <Typography variant="body2" textAlign="center">
                Este cliente ainda não possui contatos cadastrados.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactsModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderDetailsPage;
